import React, { useEffect, useMemo, useState } from "react";
import PhoneInput from "react-phone-input-2";
import { City, Country } from "country-state-city";
import "react-phone-input-2/lib/style.css";
import "../styles/payment.css";
import { supabase, SUPABASE_PUBLISHABLE_KEY, SUPABASE_URL } from "../supabase.js";
import { friendlyPaymentError } from "../lib/friendlyErrors";
const VAT_RATE = 0.15;

const translations = {
  en: {
    pageTitle: "Payment",
    pageSub: "Enter your details and your study access coupon.",
    section1: "Client Information",
    section2: "Service Details",
    section3: "Study Access",
    section4: "Summary",
    section5: "Terms",
    fullName: "Full name",
    company: "Company (optional)",
    email: "Email",
    phone: "Phone",
    country: "Country",
    city: "City",
    service: "Service",
    payMethod: "Choose payment method",
    methodCard: "Card payment",
    methodApplePay: "Apple Pay",
    amount: "Base amount (SAR)",
    discount: "Discount",
    vat: "VAT",
    total: "Total",
    coupon: "Study access coupon (required)",
    couponRequired: "A valid 100% discount coupon is required.",
    couponOnly: "Study access is currently available with a 100% discount coupon.",
    notes: "Notes (optional)",
    agree: "I agree to the terms.",
    submit: "Confirm Study Request",
    processing: "Processing...",
    successEyebrow: "Request Confirmed",
    successTitle: "Thank you for choosing Al Jabriyah Commercial Establishment",
    successText: "Your request has been received successfully. Please complete the survey so our team can start your study faster and with greater accuracy.",
    orderId: "Order ID:",
    successArabicNote: "يرجى تعبئة الاستبيان الآن حتى نبدأ العمل على طلبكم بسرعة ودقة.",
    nextSteps: "Next Steps",
    nextStep1: "Complete the survey form.",
    // nextStep2: "Our team will review your request and contact you within 24 hours.",
    startSurvey: "Start Survey Now",
    emailNoticeSent: "A thank-you email has been sent to your inbox.",
    emailNoticePending: "Email is not configured yet. The survey link below is ready.",
    successMetaService: "Requested Service",
    successMetaEmail: "Email",
    successMetaSurvey: "Survey Access",
    surveyReady: "Ready to start now",
    required: "Required",
    invalidEmail: "Enter a valid email",
    invalidPhone: "Enter a valid phone with country code",
    invalidNumber: "Unable to load the service price",
    invalidCoupon: "Invalid coupon code",
    expiredCoupon: "Coupon has expired",
    maxCoupon: "Coupon reached its usage limit",
    service1: "Technical & Operational Study",
    service2: "Market Studies & Business Analysis",
    terms: "Study access requires a valid 100% discount coupon.\nYour information is used only to process this request.\nBy submitting, you confirm that your information is accurate.",
    cardTitle: "Card Details",
    cardName: "Cardholder name",
    cardNumber: "Card number",
    cardExp: "Expiry (MM/YY)",
    cardCvv: "CVV",
    cardPlaceholder: "As shown on the card",
    cardNote: "These fields are for UI only. Real card payments require a secure payment gateway integration.",
    secureTitle: "Secure request",
    secureText: "Your information is used only to process your request.",
    selectCountry: "Select Country",
    selectCity: "Select City",
    genericError: "Something went wrong while submitting your request.",
  },
  ar: {
    pageTitle: "صفحة الدفع",
    pageSub: "أدخل بياناتك وكوبون الدخول لإنشاء الدراسة.",
    section1: "بيانات العميل",
    section2: "تفاصيل الخدمة",
    section3: "الدخول إلى الدراسة",
    section4: "ملخص الطلب",
    section5: "الشروط",
    fullName: "الاسم الكامل",
    company: "اسم الشركة (اختياري)",
    email: "البريد الإلكتروني",
    phone: "رقم الجوال",
    country: "الدولة",
    city: "المدينة",
    service: "الخدمة",
    payMethod: "اختر طريقة الدفع",
    methodCard: "الدفع بالبطاقة",
    methodApplePay: "Apple Pay",
    amount: "المبلغ الأساسي (ريال)",
    discount: "الخصم",
    vat: "الضريبة",
    total: "الإجمالي",
    coupon: "كوبون الدخول للدراسة (مطلوب)",
    couponRequired: "يلزم كوبون صالح بخصم 100% لإنشاء الدراسة.",
    couponOnly: "إنشاء الدراسة متاح حاليًا بكوبون خصم 100%.",
    notes: "ملاحظات (اختيارية)",
    agree: "أوافق على الشروط.",
    submit: "تأكيد طلب الدراسة",
    processing: "جارٍ المعالجة...",
    successEyebrow: "تم استلام الطلب",
    successTitle: "شكرًا لاختياركم الجبرية التجارية",
    successText: "تم استلام طلبكم بنجاح. يرجى تعبئة الاستبيان حتى يبدأ فريقنا العمل على الدراسة بسرعة ودقة أعلى.",
    orderId: "رقم الطلب:",
    successArabicNote: "Please complete the survey now so we can start your request promptly and accurately.",
    nextSteps: "الخطوات التالية",
    nextStep1: "قم بتعبئة نموذج الاستبيان.",
    // nextStep2: "سيقوم فريقنا بمراجعة الطلب والتواصل معكم خلال 24 ساعة.",
    startSurvey: "ابدأ الاستبيان الآن",
    emailNoticeSent: "تم إرسال رسالة شكر إلى بريدك الإلكتروني.",
    emailNoticePending: "البريد الإلكتروني غير مهيأ بعد، ورابط الاستبيان جاهز بالأسفل.",
    successMetaService: "الخدمة المطلوبة",
    successMetaEmail: "البريد الإلكتروني",
    successMetaSurvey: "حالة الاستبيان",
    surveyReady: "جاهز للبدء الآن",
    required: "مطلوب",
    invalidEmail: "أدخل بريدًا إلكترونيًا صحيحًا",
    invalidPhone: "أدخل رقم جوال صحيحًا مع مفتاح الدولة",
    invalidNumber: "تعذر تحميل سعر الخدمة",
    invalidCoupon: "كود الخصم غير صالح",
    expiredCoupon: "انتهت صلاحية كود الخصم",
    maxCoupon: "تم الوصول للحد الأقصى لاستخدام الكوبون",
    service1: "الدراسة الفنية والتشغيلية",
    service2: "دراسات السوق والتحليل التجاري",
    terms: "يتطلب إنشاء الدراسة كوبونًا صالحًا بخصم 100%.\nتُستخدم بياناتك فقط لمعالجة هذا الطلب.\nبإرسال الطلب فأنت تؤكد صحة بياناتك.",
    cardTitle: "بيانات البطاقة",
    cardName: "اسم حامل البطاقة",
    cardNumber: "رقم البطاقة",
    cardExp: "تاريخ الانتهاء (MM/YY)",
    cardCvv: "CVV",
    cardPlaceholder: "كما هو مكتوب على البطاقة",
    cardNote: "هذه الحقول للواجهة فقط. الدفع الحقيقي يحتاج إلى بوابة دفع آمنة.",
    secureTitle: "طلب آمن",
    secureText: "لن نستخدم بياناتك إلا للتواصل ومعالجة الطلب.",
    selectCountry: "اختر الدولة",
    selectCity: "اختر المدينة",
    genericError: "حدث خطأ أثناء إرسال الطلب.",
  },
};

const serviceLabelMap = {
  feasibility1: "service1",
  feasibility2: "service2",
};

const surveyPathForService = (service) =>
  service === "feasibility1" ? "/technical-survey" : "/Marketsurvey";

const normalizeValue = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

const countryAliases = {
  uae: "United Arab Emirates",
  "u.a.e": "United Arab Emirates",
  ksa: "Saudi Arabia",
  "saudi arabia": "Saudi Arabia",
  saudi: "Saudi Arabia",
};

const COMPANY_NAME_AR = "الجبرية التجارية";
const COMPANY_NAME_EN = "Al Jabriyah Commercial Establishment";
const COMPANY_LOGO_PATH = "/img/jr-watermark.png";

const resolveCountryName = (storedCountry, countries) => {
  const normalizedCountry = normalizeValue(storedCountry);
  if (!normalizedCountry) return "";

  const aliasMatch = countryAliases[normalizedCountry];
  if (aliasMatch) return aliasMatch;

  const directMatch = countries.find(
    (country) =>
      normalizeValue(country.name) === normalizedCountry ||
      normalizeValue(country.isoCode) === normalizedCountry
  );

  return directMatch?.name || storedCountry;
};

function PaymentPage({ language = "en" }) {
  const t = useMemo(() => translations[language] || translations.en, [language]);
  const isArabic = language === "ar";
  const countries = useMemo(() => Country.getAllCountries(), []);

  const [form, setForm] = useState({
    fullName: "",
    company: "",
    email: "",
    phone: "",
    country: "Saudi Arabia",
    city: "",
    service: "feasibility2",
    coupon: "",
    notes: "",
    agree: false,
  });
  const [cities, setCities] = useState([]);
  const [couponData, setCouponData] = useState(null);
  const [priceData, setPriceData] = useState({ base: 0, discount: 0, vat: 0, total: 0 });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [orderId, setOrderId] = useState("");
  const [surveyUrl, setSurveyUrl] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");
useEffect(() => {
  const protectPaymentPage = async () => {
    const { data } = await supabase.auth.getSession();

    if (!data.session) {
      window.location.replace("/login");
      return;
    }

    const termsAccepted =
      localStorage.getItem("terms_feasibility_accepted") === "true";

    if (!termsAccepted) {
      window.location.replace("/terms/feasibility");
    }
  };

  protectPaymentPage();
}, []);
  const sendThankYouEmail = async ({ customerName, customerEmail, orderReference, surveyLink, serviceName }) => {
    const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
    if (sessionError) throw sessionError;

    const accessToken = sessionData?.session?.access_token;
    if (!accessToken) {
      throw new Error("No active Supabase session found for sending email.");
    }

    const response = await fetch(`${SUPABASE_URL}/functions/v1/send-email`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: SUPABASE_PUBLISHABLE_KEY,
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        customer_name: customerName,
        customer_email: customerEmail,
        order_id: orderReference,
        service_name: serviceName,
        survey_url: surveyLink,
        company_name: COMPANY_NAME_AR,
        company_name_en: COMPANY_NAME_EN,
        logo_url: `${window.location.origin}${COMPANY_LOGO_PATH}`,
        email_subject: `${COMPANY_NAME_EN} | Thank You - Please Complete The Survey`,
        headline_ar: "شكرًا لثقتكم في الجبرية التجارية",
        headline_en: "Thank you for choosing Al Jabriyah Commercial Establishment",
        body_ar:
          `عميلنا العزيز ${customerName}،\n\nتم استلام طلبكم بنجاح، ورقم الطلب هو ${orderReference}.\nيرجى تعبئة الاستبيان عبر الرابط التالي حتى نبدأ العمل على طلبكم بسرعة ودقة:\n${surveyLink}\n\nمع خالص الشكر،\n${COMPANY_NAME_AR}`,
        body_en:
          `Dear ${customerName},\n\nYour request has been received successfully. Your order reference is ${orderReference}.\nPlease complete the survey using the link below so we can start your request quickly and accurately:\n${surveyLink}\n\nBest regards,\n${COMPANY_NAME_EN}`,
        cta_label_ar: "تعبئة الاستبيان",
        cta_label_en: "Complete The Survey",
      }),
    });

    if (!response.ok) {
      const errorBody = await response.text();
      throw new Error(`send-email failed: ${response.status} ${errorBody}`);
    }

    return true;
  };

  useEffect(() => {
    const selectedCountry = countries.find((country) => country.name === form.country);
    setCities(selectedCountry ? City.getCitiesOfCountry(selectedCountry.isoCode) || [] : []);
  }, [countries, form.country]);

  useEffect(() => {
    const loadUserData = async () => {
      const { data: userData } = await supabase.auth.getUser();
      const user = userData?.user;
      if (!user) return;

      const { data: profile } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", user.id)
        .single();

      const resolvedCountry = resolveCountryName(profile?.country, countries);

      setForm((prev) => ({
        ...prev,
        fullName: `${profile?.first_name || ""} ${profile?.last_name || ""}`.trim() || prev.fullName,
        email: user.email || prev.email,
        phone: profile?.phone || prev.phone,
        country: resolvedCountry || prev.country,
        city: profile?.city || prev.city,
      }));
    };

    loadUserData();
  }, [countries]);

  useEffect(() => {
    let cancelled = false;
    setPriceData({ base: 0, discount: 0, vat: 0, total: 0 });
    const fetchPricing = async () => {
      const { data: service } = await supabase
        .from("services")
        .select("price")
        .eq("id", form.service)
        .single();

      if (cancelled) return;
      if (!service?.price) {
        setPriceData({ base: 0, discount: 0, vat: 0, total: 0 });
        return;
      }

      const rawDiscount = couponData
        ? couponData.type === "percent"
          ? (service.price * couponData.value) / 100
          : couponData.value
        : 0;
      const discount = Math.min(rawDiscount, service.price);
      const subtotal = service.price - discount;
      const vat = subtotal * VAT_RATE;

      setPriceData({
        base: service.price,
        discount,
        vat,
        total: subtotal + vat,
      });
    };

    fetchPricing();
    return () => { cancelled = true; };
  }, [form.service, couponData]);

  useEffect(() => {
    const couponCode = form.coupon.trim().toUpperCase();
    let cancelled = false;
    setCouponData(null);

    if (!couponCode) {
      setCouponData(null);
      setErrors((prev) => {
        const next = { ...prev };
        delete next.coupon;
        return next;
      });
      return;
    }

    const timer = setTimeout(async () => {
      const { data, error } = await supabase
        .from("coupons")
        .select("*")
        .eq("code", couponCode)
        .eq("active", true)
        .single();

      if (cancelled) return;

      if (error || !data) {
        setCouponData(null);
        setErrors((prev) => ({ ...prev, coupon: t.invalidCoupon }));
        return;
      }

      const expired = data.expires_at && new Date(data.expires_at) < new Date();
      if (expired) {
        setCouponData(null);
        setErrors((prev) => ({ ...prev, coupon: t.expiredCoupon }));
        return;
      }

      if (data.max_uses && data.used_count >= data.max_uses) {
        setCouponData(null);
        setErrors((prev) => ({ ...prev, coupon: t.maxCoupon }));
        return;
      }

      if (data.type !== "percent" || Number(data.value) !== 100) {
        setCouponData(null);
        setErrors((prev) => ({ ...prev, coupon: t.couponRequired }));
        return;
      }

      setCouponData(data);
      setErrors((prev) => {
        const next = { ...prev };
        delete next.coupon;
        return next;
      });
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [form.coupon, t.expiredCoupon, t.invalidCoupon, t.maxCoupon, t.couponRequired]);

  const hasAccessCoupon = Boolean(
    couponData && couponData.code === form.coupon.trim().toUpperCase() &&
    couponData.type === "percent" && Number(couponData.value) === 100
  );

  const handleFieldChange = (event) => {
    const { name, value, type, checked } = event.target;
    if (name === "coupon") setCouponData(null);
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleCountryChange = (event) => {
    const value = event.target.value;
    setForm((prev) => ({
      ...prev,
      country: value,
      city: "",
    }));
  };

  const validate = () => {
    const nextErrors = {};

    if (!form.fullName.trim()) nextErrors.fullName = t.required;
    if (!form.email.trim()) nextErrors.email = t.required;
    else if (!/^\S+@\S+\.\S+$/.test(form.email)) nextErrors.email = t.invalidEmail;

    if (!form.phone.trim()) nextErrors.phone = t.required;
    else if (!/^\+\d{10,15}$/.test(form.phone)) nextErrors.phone = t.invalidPhone;

    if (!form.country.trim()) nextErrors.country = t.required;
    if (!form.city.trim()) nextErrors.city = t.required;
    if (!priceData.base || priceData.base <= 0) nextErrors.baseAmount = t.invalidNumber;

    if (!form.agree) nextErrors.agree = t.required;
    if (!hasAccessCoupon) nextErrors.coupon = errors.coupon || t.couponRequired;

    return nextErrors;
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    if (loading) return;
    setSubmitError("");

    const validationErrors = validate();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length) return;

    setLoading(true);

    try {
      const { data: verifiedCoupon, error: couponError } = await supabase
        .from("coupons")
        .select("*")
        .eq("code", form.coupon.trim().toUpperCase())
        .eq("active", true)
        .single();
      const couponFailure = couponError || !verifiedCoupon
        ? t.invalidCoupon
        : verifiedCoupon.expires_at && new Date(verifiedCoupon.expires_at) <= new Date()
          ? t.expiredCoupon
          : verifiedCoupon.max_uses && verifiedCoupon.used_count >= verifiedCoupon.max_uses
            ? t.maxCoupon
            : verifiedCoupon.type !== "percent" || Number(verifiedCoupon.value) !== 100
              ? t.couponRequired
              : null;
      if (couponFailure) {
        setCouponData(null);
        setErrors((prev) => ({ ...prev, coupon: couponFailure }));
        return;
      }

      const { data: userData } = await supabase.auth.getUser();
      const user = userData?.user;
      if (!user?.id) {
        throw new Error("You must be logged in before submitting a payment request.");
      }

      const { data, error } = await supabase.rpc("create_coupon_study", {
          p_request: {
            language,
            full_name: form.fullName,
            company: form.company || null,
            email: form.email,
            phone: form.phone,
            country: form.country,
            city: form.city,
            service: form.service,
            coupon_code: verifiedCoupon.code,
            notes: form.notes || null,
          },
        });

      if (error) throw error;

      const createdStudyId = data.study_id;

      const finalOrderId = data?.request_ref || `REQ-${data?.id}`;
      setOrderId(finalOrderId);
      localStorage.setItem("orderId", finalOrderId);

      const surveyParams = new URLSearchParams();
      if (createdStudyId) surveyParams.set("study_id", createdStudyId);
      if (data?.request_ref) surveyParams.set("request_ref", data.request_ref);
      const nextSurveyUrl = `${surveyPathForService(form.service)}?${surveyParams.toString()}`;
      setSurveyUrl(nextSurveyUrl);
      setSubmitted(true);

      try {
        const emailWasSent = await sendThankYouEmail({
          customerName: form.fullName,
          customerEmail: form.email,
          orderReference: finalOrderId,
          surveyLink: `${window.location.origin}${nextSurveyUrl}`,
          serviceName: serviceLabel,
        });

        setEmailSent(emailWasSent);
      } catch (emailError) {
        console.error("Thank-you email failed:", emailError);
        setEmailSent(false);
      }
    } catch (error) {
      console.error("Payment request failed:", error);
      const couponMessages = {
        COUPON_INVALID: t.invalidCoupon,
        COUPON_EXPIRED: t.expiredCoupon,
        COUPON_LIMIT: t.maxCoupon,
        COUPON_FULL_DISCOUNT_REQUIRED: t.couponRequired,
      };
      setSubmitError(couponMessages[error.message] || friendlyPaymentError(error, t.genericError));
    } finally {
      setLoading(false);
    }
  };

  const serviceLabel = t[serviceLabelMap[form.service]] || form.service;

  return (
    <div className="payment-page" lang={language} dir={isArabic ? "rtl" : "ltr"}>
      <section className="pay-hero">
        <div className="pay-hero-inner">
          <h1 className="pay-title">{t.pageTitle}</h1>
          <p className="pay-sub">{t.pageSub}</p>
        </div>
      </section>

      <section className="pay-section">
        <div className="pay-container">
          {submitted ? (
            <div className="pay-card pay-success">
              <div className="pay-success-brand">
                <img src={COMPANY_LOGO_PATH} alt={COMPANY_NAME_EN} className="pay-success-logo" />
                <div className="pay-check">✓</div>
              </div>

              <span className="pay-success-eyebrow">{t.successEyebrow}</span>
              <h2 className="pay-success-title">{t.successTitle}</h2>
              <p className="pay-success-text">{t.successText}</p>
              <p className="pay-success-translation">{t.successArabicNote}</p>

              <div className="pay-success-meta">
                {orderId ? (
                  <div className="pay-success-chip">
                    <span>{t.orderId}</span>
                    <strong>{orderId}</strong>
                  </div>
                ) : null}

                <div className="pay-success-chip">
                  <span>{t.successMetaService}</span>
                  <strong>{serviceLabel}</strong>
                </div>

                <div className="pay-success-chip">
                  <span>{t.successMetaEmail}</span>
                  <strong>{form.email}</strong>
                </div>

                <div className="pay-success-chip">
                  <span>{t.successMetaSurvey}</span>
                  <strong>{t.surveyReady}</strong>
                </div>
              </div>

              <div className={`pay-email-status ${emailSent ? "sent" : "pending"}`}>
                {emailSent ? t.emailNoticeSent : t.emailNoticePending}
              </div>

              <div className="next-steps">
                <h4>{t.nextSteps}</h4>
                <ul>
                  <li>{t.nextStep1}</li>
                  <li>{t.nextStep2}</li>
                </ul>
              </div>

              <a className="pay-start-survey" href={surveyUrl || surveyPathForService(form.service)}>
                {t.startSurvey}
              </a>
            </div>
          ) : (
            <form className="pay-layout" onSubmit={onSubmit}>
              <div className="pay-card">
                <div className="pay-block">
                  <h2 className="pay-h2">{t.section1}</h2>
                  <div className="pay-grid">
                    <div className="field">
                      <label>{t.fullName}</label>
                      <input type="text" name="fullName" value={form.fullName} onChange={handleFieldChange} className={errors.fullName ? "input-error" : ""} placeholder={t.fullName} />
                      {errors.fullName ? <span className="err">{errors.fullName}</span> : null}
                    </div>

                    <div className="field">
                      <label>{t.company}</label>
                      <input name="company" value={form.company} onChange={handleFieldChange} />
                    </div>

                    <div className="field">
                      <label>{t.email}</label>
                      <input name="email" value={form.email} onChange={handleFieldChange} className={errors.email ? "input-error" : ""} placeholder="name@email.com" />
                      {errors.email ? <span className="err">{errors.email}</span> : null}
                    </div>

                    <div className="field">
                      <label>{t.phone}</label>
                      <PhoneInput country="sa" value={form.phone.replace(/^\+/, "")} onChange={(phone) => setForm((prev) => ({ ...prev, phone: phone ? `+${phone}` : "" }))} inputClass={errors.phone ? "input-error" : ""} containerClass="phone-container" inputStyle={{ width: "100%", height: "45px" }} />
                      {errors.phone ? <span className="err">{errors.phone}</span> : null}
                    </div>

                    <div className="field">
                      <label>{t.country}</label>
                      <select name="country" value={form.country} onChange={handleCountryChange} className={errors.country ? "input-error" : ""}>
                        <option value="">{t.selectCountry}</option>
                        {countries.map((country) => (
                          <option key={country.isoCode} value={country.name}>{country.flag} {country.name}</option>
                        ))}
                      </select>
                      {errors.country ? <span className="err">{errors.country}</span> : null}
                    </div>

                    <div className="field">
                      <label>{t.city}</label>
                      <select name="city" value={form.city} onChange={handleFieldChange} className={errors.city ? "input-error" : ""} disabled={!form.country}>
                        <option value="">{t.selectCity}</option>
                        {cities.map((city) => (
                          <option key={`${city.name}-${city.countryCode}`} value={city.name}>{city.name}</option>
                        ))}
                      </select>
                      {errors.city ? <span className="err">{errors.city}</span> : null}
                    </div>
                  </div>
                </div>

                <div className="pay-divider" />

                <div className="pay-block">
                  <h2 className="pay-h2">{t.section2}</h2>
                  <div className="pay-grid">
                    <div className="field">
                      <label>{t.service}</label>
                      <select name="service" value={form.service} onChange={handleFieldChange} disabled={loading}>
                        <option value="feasibility2">{t.service2}</option>
                        <option value="feasibility1">{t.service1}</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="pay-divider" />

                <div className="pay-block">
                  <h2 className="pay-h2">{t.section3}</h2>
                  <p>{t.couponOnly}</p>

                  <div className="field" style={{ marginTop: 14 }}>
                    <label>{t.notes}</label>
                    <textarea name="notes" value={form.notes} onChange={handleFieldChange} rows={4} />
                  </div>
                </div>

                <div className="pay-divider" />

                <div className="pay-block">
                  <h2 className="pay-h2">{t.section5}</h2>
                  <pre className="pay-terms" style={{ direction: isArabic ? "rtl" : "ltr", textAlign: isArabic ? "right" : "left" }}>{t.terms}</pre>

                  <div className="agree">
                    <label>
                      <input type="checkbox" name="agree" checked={form.agree} onChange={handleFieldChange} />
                      <span>{t.agree}</span>
                    </label>
                    {errors.agree ? <span className="err">{errors.agree}</span> : null}
                  </div>

                  {submitError ? <span className="err">{submitError}</span> : null}

                  <button className="pay-btn" type="submit" disabled={loading || !hasAccessCoupon || priceData.base <= 0}>
                    {loading ? t.processing : t.submit}
                  </button>
                </div>
              </div>

              <aside className="pay-card pay-summary">
                <h2 className="pay-h2">{t.section4}</h2>

                <div className="sum-row">
                  <span>{t.service}</span>
                  <strong>{serviceLabel}</strong>
                </div>

                <div className="sum-divider" />

                <div className="field">
                  <label>{t.amount}</label>
                  <input value={priceData.base.toFixed(2)} readOnly />
                  {errors.baseAmount ? <span className="err">{errors.baseAmount}</span> : null}
                </div>

                <div className="field">
                  <label>{t.coupon}</label>
                  <input name="coupon" value={form.coupon} onChange={handleFieldChange} placeholder="CODE" required disabled={loading} className={errors.coupon ? "input-error" : ""} />
                  {errors.coupon ? <span className="err">{errors.coupon}</span> : null}
                </div>

                <div className="sum-divider" />

                {priceData.discount > 0 ? (
                  <div className="sum-row">
                    <span>{t.discount}</span>
                    <strong>{priceData.discount.toFixed(2)} SAR</strong>
                  </div>
                ) : null}

                <div className="sum-row">
                  <span>{t.vat} (15%)</span>
                  <strong>{priceData.vat.toFixed(2)} SAR</strong>
                </div>

                <div className="sum-row total">
                  <span>{t.total}</span>
                  <strong>{priceData.total.toFixed(2)} SAR</strong>
                </div>

                <div className="secure">
                  <div className="secure-badge">SSL</div>
                  <div>
                    <div className="secure-title">{t.secureTitle}</div>
                    <div className="secure-sub">{t.secureText}</div>
                  </div>
                </div>
              </aside>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

export default PaymentPage;
